"use client";
import { useState, useEffect } from "react";
import { useTwilioDevice } from "@/hooks/useTwilioDevice";
import { CallPopup } from "@components/CallPopup";
import useAuthStore from "@auth/store/useAuthStore";
import { fetchTransferCall } from "@api/inboxRequest/inboxRequest";
import ModalError from "@components/modals/ModalError";

interface TwilioProviderProps {
  tokenTwilio: string;
}

export function TwilioProvider({ tokenTwilio }: TwilioProviderProps) {
  const { user, token } = useAuthStore();
  const [incomingConnection, setIncomingConnection] = useState(null as any);
  const [outgoingConnection, setOutgoingConnection] = useState(null);
  const [showModalError, setShowModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  
  const {
    acceptCall,
    rejectCall,
    callFrom,
    hangUpCall,
    muteCall,
    unmuteCall,
  } = useTwilioDevice(tokenTwilio, incomingConnection, setIncomingConnection, outgoingConnection, setOutgoingConnection);

  const handleTransfer = async (userId: string) => {
    if (!incomingConnection) return;
    const callSid = incomingConnection.parameters?.CallSid;
    if (!callSid || !user?.supplier_uuid) return;

    try {
      const resp = await fetchTransferCall(token, callSid, user.supplier_uuid, userId, user?.id);
      if (resp.status) {
        hangUpCall();
      } else {
        console.error(resp.error || "Failed to transfer call");
        setErrorMessage(resp.error || "Failed to transfer call");
        setShowModalError(true);
      }
    } catch (e) {
      console.error("Failed to transfer call");
      setErrorMessage("Failed to transfer call");
      setShowModalError(true);
    }
  };

  useEffect(() => {
    const handleOutgoingStart = (e) => {
      setOutgoingConnection(e.detail);
    };

    const handleOutgoingEnd = () => {
      setOutgoingConnection(null);
    };

    window.addEventListener("twilio-outgoing-call-started", handleOutgoingStart);
    window.addEventListener("twilio-outgoing-call-ended", handleOutgoingEnd);

    return () => {
      window.removeEventListener("twilio-outgoing-call-started", handleOutgoingStart);
      window.removeEventListener("twilio-outgoing-call-ended", handleOutgoingEnd);
    };
  }, []);

  if (!incomingConnection && !outgoingConnection) return null;

  return (
    <>
      <CallPopup
        callerName={incomingConnection ? callFrom : outgoingConnection?.to}
        onAccept={acceptCall}
        onHangUp={() => {
          hangUpCall();
          setOutgoingConnection(null);
        }}
        onReject={rejectCall}
        onTransfer={handleTransfer}
        usersToken={token}
        supplierUuid={user?.supplier_uuid}
        muteCall={muteCall}
        unMuteCall={unmuteCall}
        isOutgoing={!!outgoingConnection}
      />
      <ModalError
        isvisible={showModalError}
        onClose={() => setShowModalError(false)}
        title="Error Transferring Call"
        message={errorMessage}
      />
    </>
  );
}